import axios from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import { GridColDef } from '@mui/x-data-grid-pro';
import { gridDateTimeFormatter, withQueryParams } from '@shared/commonFunctions';
import { gridCurrencyCell, dataGridCol } from '@shared/components/grid/common';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { SERVICES } from '@shared/constants/apiConfig';
import { useServerSide } from '@shared/hooks/useServerSide';
import { Wrapper } from '@styles/CommonStyles';
import { useTabs } from '@shared/hooks/useTabs';

interface IDealSheet {
  dealSheetId: string;
  createdDate: string;
  customerName: string;
  contactId: string;
  vehicleSellingPrice: number;
  dealId: string;
  lcName: string;
  cudlAmountFinanced: number;
  amount: number;
  payoff: number;
  adjustedPayoff: number;
  totalGrossProfit: number;
  commission: number;
}

export const DealSheetsTable = () => {
  const navigate = useNavigate();
  const { openTab } = useTabs();
  const { requestPayload, onSortModelChange, onFilterModelChange, onPageChange, handleClosePanel } = useServerSide();

  const [totalCount, setTotalCount] = useState<number>(0);
  const [items, setItems] = useState<IDealSheet[]>([]);

  const {
    data: dealSheets,
    mutate,
    isValidating,
  } = useSWR(
    `/${SERVICES.SEARCH}/search/dealsheetsearch`,
    (url: string) =>
      axios.post(url, {
        ...requestPayload,
        pageSize: 15,
      }),
    {
      shouldRetryOnError: false,
    }
  );

  useEffect(() => {
    mutate();
  }, [requestPayload]);

  useEffect(() => {
    if (dealSheets) {
      setItems(dealSheets.data?.results);
      setTotalCount(dealSheets.data?.totalCount);
    }
  }, [dealSheets]);

  const columns: GridColDef[] = useMemo(() => (
    [
      dataGridCol('numericId', {
        headerName: 'Deal Sheet ID',
        minWidth: 70,
      }),
      dataGridCol('createdDate', {
        minWidth:105,
        headerName: 'Date',
        valueFormatter: gridDateTimeFormatter,
        type: 'dateTime',
      }),
      dataGridCol('dealNumericId', { minWidth: 150 }),
      dataGridCol('customerName', { minWidth: 280 }),
      dataGridCol('lcName', {
        headerName: 'LC Name',
        minWidth: 160,
      }),
      dataGridCol('cudlAmountFinanced', {
        minWidth:105,
        ...gridCurrencyCell,
        headerName: 'CUDL Amount',
        type: 'number',
      }),
      dataGridCol('vehicleSellingPrice', {
        headerName: 'Portal Amount',
        ...gridCurrencyCell,
        type: 'number'
      }),
      dataGridCol('payOff', {
        minWidth: 110,
        ...gridCurrencyCell,
        type: 'number'
      }),
      dataGridCol('adjustedPayoff', {
        minWidth: 110,
        ...gridCurrencyCell,
        type: 'number'
      }),
      dataGridCol('totalGrossProfit', {
        headerName: 'Profit',
        minWidth: 100,
        ...gridCurrencyCell,
        type: 'number'
      }),
      dataGridCol('commission', {
        minWidth: 75,
        ...gridCurrencyCell,
        type: 'number'
      }),
    ].map(i => ({ minWidth: 120, ...i, align: 'left', headerAlign: 'left' }))
  ), []);

  const onRowClick = useCallback(({ row }: { row: IDealSheet }) => {
    const route = withQueryParams('/deal-sheet', { id: row.contactId, dealId: row?.dealId });
    const title = `${row?.customerName} - Deal Sheet`;
    openTab({ title, route });
    navigate(route);
  }, []);

  return (
    <Wrapper>
      <PhoenixGrid
        autoHeight
        canNotCopy
        noButton
        withHeader
        disableSelectionOnClick
        pageSize={15}
        name="Deal Sheet"
        columns={columns}
        rows={items && !isValidating ? items : []}
        loading={!items || isValidating}
        rowCount={totalCount}
        paginationMode="server"
        sortingMode="server"
        filterMode="server"
        getRowId={(row) => row.dealSheetId}
        onSortModelChange={onSortModelChange}
        onFilterModelChange={onFilterModelChange}
        onPageChange={onPageChange}
        onRowClick={onRowClick}
        onPreferencePanelClose={handleClosePanel}
      />
    </Wrapper>
  );
};
